// src/components/admin/PFManagement.js
import React, { useState, useEffect } from 'react';
import {
    Wallet, Download, Users, TrendingUp, Building2,
    Search, Filter, Calendar, FileText, Settings, Shield
} from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { api } from '../../services/api';

const PFManagement = () => {
    const { theme } = useTheme();
    const [activeTab, setActiveTab] = useState('OVERVIEW');
    const [employees, setEmployees] = useState([]);
    const [employeesWithPF, setEmployeesWithPF] = useState([]);
    const [pfReports, setPfReports] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedMonth, setSelectedMonth] = useState('2024-11');
    const [loading, setLoading] = useState(false);
    const [pfEnabled, setPfEnabled] = useState(true);
    const [savingConfig, setSavingConfig] = useState(false);
    const [selectedEmployee, setSelectedEmployee] = useState(null);
    const [showDetailsModal, setShowDetailsModal] = useState(false);
    const [pfDetails, setPfDetails] = useState(null);
    const [pfHistory, setPfHistory] = useState([]);
    const [loadingDetails, setLoadingDetails] = useState(false);

    const tabs = ['OVERVIEW', 'EMPLOYEE PF', 'REPORTS', 'CONFIGURATION'];

    // Fetch all employees PF data
    const fetchEmployeesPF = async () => {
        setLoading(true);
        try {
            // Fetch all employees
            const response = await api.get('/api/employees');
            if (response.data.success) {
                const allEmployees = response.data.employees || [];
                setEmployees(allEmployees);

                // Fetch PF balance for each employee
                const employeesWithBalances = await Promise.all(
                    allEmployees.map(async (emp) => {
                        try {
                            const balanceResponse = await api.get(`/api/pf/balance/${emp.id}`);
                            if (balanceResponse.data.success) {
                                return {
                                    ...emp,
                                    pf_balance: balanceResponse.data.data
                                };
                            }
                        } catch (err) {
                            console.error(`Error fetching PF balance for ${emp.id}:`, err);
                        }
                        return {
                            ...emp,
                            pf_balance: null
                        };
                    })
                );

                setEmployeesWithPF(employeesWithBalances);
            }
        } catch (err) {
            console.error('Error fetching employees PF data:', err);
        } finally {
            setLoading(false);
        }
    };

    // Fetch individual employee PF details
    const fetchEmployeePFDetails = async (employeeId) => {
        setLoadingDetails(true);
        try {
            // Fetch PF passbook (balance + history)
            const passbookResponse = await api.get(`/api/pf/passbook/${employeeId}`, {
                params: { financialYear: '2024-25' }
            });

            if (passbookResponse.data.success) {
                const data = passbookResponse.data.data;
                setPfDetails(data.balance);
                setPfHistory(data.passbook_entries || []);
                setSelectedEmployee(data.employee);
                setShowDetailsModal(true);
            }
        } catch (err) {
            console.error('Error fetching employee PF details:', err);
            alert('Failed to fetch PF details. Please try again.');
        } finally {
            setLoadingDetails(false);
        }
    };

    // Fetch PF reports
    const fetchPFReports = async () => {
        setLoading(true);
        try {
            const response = await api.get('/api/pf/reports/monthly', {
                params: { month: selectedMonth }
            });
            if (response.data.success) {
                setPfReports(response.data.data);
            }
        } catch (err) {
            console.error('Error fetching PF reports:', err);
        } finally {
            setLoading(false);
        }
    };

    // Fetch PF configuration
    const fetchPFConfig = async () => {
        try {
            // Use hardcoded company ID (same as in backend scripts)
            const companyId = 'b52eee49-1e89-414d-9e51-f2747549ad08';

            const response = await api.get(`/api/pf/configuration/${companyId}`);
            if (response.data.success) {
                setPfEnabled(response.data.data?.is_pf_applicable ?? true);
            }
        } catch (err) {
            console.error('Error fetching PF config:', err);
            // Default to enabled if fetch fails
            setPfEnabled(true);
        }
    };

    // Save PF configuration
    const savePFConfig = async () => {
        setSavingConfig(true);
        try {
            // Use hardcoded company ID (same as in backend scripts)
            const companyId = 'b52eee49-1e89-414d-9e51-f2747549ad08';

            const response = await api.put(`/api/pf/configuration/${companyId}`, {
                is_pf_applicable: pfEnabled
            });

            if (response.data.success) {
                alert(response.data.message || 'PF configuration saved successfully!');
                console.log('✅ PF configuration updated:', {
                    is_pf_applicable: pfEnabled,
                    status: pfEnabled ? 'ENABLED' : 'DISABLED'
                });
            }
        } catch (err) {
            console.error('Error saving PF config:', err);
            alert('Failed to save PF configuration. Please try again.');
        } finally {
            setSavingConfig(false);
        }
    };

    useEffect(() => {
        fetchEmployeesPF();
        fetchPFConfig();
    }, []);

    const formatCurrency = (amount) => {
        if (!amount && amount !== 0) return '₹0';
        return `₹${parseFloat(amount).toLocaleString('en-IN', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        })}`;
    };

    // Download PF Challan
    const downloadPFChallan = async () => {
        try {
            const response = await api.get('/api/pf/reports/challan', {
                params: { month: selectedMonth },
                responseType: 'blob'
            });

            const blob = new Blob([response.data], { type: 'application/pdf' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `PF_Challan_${selectedMonth}.pdf`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (err) {
            console.error('Error downloading PF challan:', err);
            alert('Failed to download PF challan. Please try again.');
        }
    };

    const renderOverview = () => (
        <div className="space-y-6">
            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className={`p-6 rounded-lg border ${
                    theme === 'dark'
                        ? 'bg-gradient-to-br from-blue-900/30 to-blue-800/20 border-blue-700/30'
                        : 'bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200'
                }`}>
                    <Users className={`w-8 h-8 mb-3 ${
                        theme === 'dark' ? 'text-blue-400' : 'text-blue-600'
                    }`} />
                    <h3 className={`text-sm font-medium mb-1 ${
                        theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                    }`}>
                        Total PF Members
                    </h3>
                    <p className={`text-2xl font-bold ${
                        theme === 'dark' ? 'text-white' : 'text-gray-900'
                    }`}>
                        {employees.length}
                    </p>
                </div>

                <div className={`p-6 rounded-lg border ${
                    theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
                }`}>
                    <Building2 className={`w-8 h-8 mb-3 ${
                        theme === 'dark' ? 'text-green-400' : 'text-green-600'
                    }`} />
                    <h3 className={`text-sm font-medium mb-1 ${
                        theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                    }`}>
                        Monthly PF Liability
                    </h3>
                    <p className={`text-2xl font-bold ${
                        theme === 'dark' ? 'text-white' : 'text-gray-900'
                    }`}>
                        {formatCurrency(pfReports?.total_monthly_pf || 0)}
                    </p>
                </div>

                <div className={`p-6 rounded-lg border ${
                    theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
                }`}>
                    <Wallet className={`w-8 h-8 mb-3 ${
                        theme === 'dark' ? 'text-purple-400' : 'text-purple-600'
                    }`} />
                    <h3 className={`text-sm font-medium mb-1 ${
                        theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                    }`}>
                        Employee Contribution
                    </h3>
                    <p className={`text-2xl font-bold ${
                        theme === 'dark' ? 'text-white' : 'text-gray-900'
                    }`}>
                        {formatCurrency(pfReports?.total_employee_contribution || 0)}
                    </p>
                </div>

                <div className={`p-6 rounded-lg border ${
                    theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
                }`}>
                    <Shield className={`w-8 h-8 mb-3 ${
                        theme === 'dark' ? 'text-amber-400' : 'text-amber-600'
                    }`} />
                    <h3 className={`text-sm font-medium mb-1 ${
                        theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                    }`}>
                        Employer Contribution
                    </h3>
                    <p className={`text-2xl font-bold ${
                        theme === 'dark' ? 'text-white' : 'text-gray-900'
                    }`}>
                        {formatCurrency(pfReports?.total_employer_contribution || 0)}
                    </p>
                </div>
            </div>

            {/* Quick Actions */}
            <div className={`p-6 rounded-lg border ${
                theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
            }`}>
                <h3 className={`text-lg font-semibold mb-4 ${
                    theme === 'dark' ? 'text-white' : 'text-gray-900'
                }`}>
                    Quick Actions
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <button
                        onClick={downloadPFChallan}
                        className={`flex items-center gap-3 p-4 rounded-lg border transition-colors ${
                            theme === 'dark'
                                ? 'border-gray-700 hover:bg-gray-700/50'
                                : 'border-gray-200 hover:bg-gray-50'
                        }`}
                    >
                        <Download className="w-5 h-5 text-blue-500" />
                        <div className="text-left">
                            <p className={`font-medium ${
                                theme === 'dark' ? 'text-white' : 'text-gray-900'
                            }`}>
                                Download PF Challan
                            </p>
                            <p className={`text-sm ${
                                theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                            }`}>
                                For {selectedMonth}
                            </p>
                        </div>
                    </button>

                    <button
                        onClick={() => setActiveTab('REPORTS')}
                        className={`flex items-center gap-3 p-4 rounded-lg border transition-colors ${
                            theme === 'dark'
                                ? 'border-gray-700 hover:bg-gray-700/50'
                                : 'border-gray-200 hover:bg-gray-50'
                        }`}
                    >
                        <FileText className="w-5 h-5 text-green-500" />
                        <div className="text-left">
                            <p className={`font-medium ${
                                theme === 'dark' ? 'text-white' : 'text-gray-900'
                            }`}>
                                Generate Reports
                            </p>
                            <p className={`text-sm ${
                                theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                            }`}>
                                Monthly & annual
                            </p>
                        </div>
                    </button>

                    <button
                        onClick={() => setActiveTab('CONFIGURATION')}
                        className={`flex items-center gap-3 p-4 rounded-lg border transition-colors ${
                            theme === 'dark'
                                ? 'border-gray-700 hover:bg-gray-700/50'
                                : 'border-gray-200 hover:bg-gray-50'
                        }`}
                    >
                        <Settings className="w-5 h-5 text-purple-500" />
                        <div className="text-left">
                            <p className={`font-medium ${
                                theme === 'dark' ? 'text-white' : 'text-gray-900'
                            }`}>
                                PF Configuration
                            </p>
                            <p className={`text-sm ${
                                theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                            }`}>
                                Rates & settings
                            </p>
                        </div>
                    </button>
                </div>
            </div>
        </div>
    );

    const renderEmployeePF = () => {
        const filteredEmployees = employeesWithPF.filter(emp =>
            emp.first_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            emp.last_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            emp.employee_code?.toLowerCase().includes(searchTerm.toLowerCase())
        );

        return (
            <div className="space-y-4">
                {/* Search Bar */}
                <div className={`flex items-center gap-4 p-4 rounded-lg border ${
                    theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
                }`}>
                    <div className="flex-1 relative">
                        <Search className={`absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 ${
                            theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                        }`} />
                        <input
                            type="text"
                            placeholder="Search by name or employee code..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className={`w-full pl-10 pr-4 py-2 rounded-lg border ${
                                theme === 'dark'
                                    ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                                    : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                            }`}
                        />
                    </div>
                </div>

                {/* Employee PF Table */}
                <div className={`overflow-x-auto rounded-lg border ${
                    theme === 'dark' ? 'border-gray-700' : 'border-gray-200'
                }`}>
                    <table className="w-full">
                        <thead className={theme === 'dark' ? 'bg-gray-800' : 'bg-gray-50'}>
                            <tr>
                                <th className={`px-4 py-3 text-left text-xs font-semibold ${
                                    theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                                }`}>Employee</th>
                                <th className={`px-4 py-3 text-left text-xs font-semibold ${
                                    theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                                }`}>UAN</th>
                                <th className={`px-4 py-3 text-right text-xs font-semibold ${
                                    theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                                }`}>EPF Balance</th>
                                <th className={`px-4 py-3 text-right text-xs font-semibold ${
                                    theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                                }`}>EPS Balance</th>
                                <th className={`px-4 py-3 text-right text-xs font-semibold ${
                                    theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                                }`}>Interest Earned</th>
                                <th className={`px-4 py-3 text-right text-xs font-semibold ${
                                    theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                                }`}>Total Balance</th>
                                <th className={`px-4 py-3 text-center text-xs font-semibold ${
                                    theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                                }`}>Actions</th>
                            </tr>
                        </thead>
                        <tbody className={`divide-y ${
                            theme === 'dark' ? 'divide-gray-700' : 'divide-gray-200'
                        }`}>
                            {loading ? (
                                <tr>
                                    <td colSpan="7" className="px-4 py-8 text-center">
                                        <div className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                                            Loading employees PF data...
                                        </div>
                                    </td>
                                </tr>
                            ) : filteredEmployees.length === 0 ? (
                                <tr>
                                    <td colSpan="7" className="px-4 py-8 text-center">
                                        <div className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                                            No employees found
                                        </div>
                                    </td>
                                </tr>
                            ) : (
                                filteredEmployees.map((employee) => {
                                    const balance = employee.pf_balance;
                                    return (
                                        <tr key={employee.id} className={
                                            theme === 'dark' ? 'bg-gray-800/50 hover:bg-gray-700/50' : 'bg-white hover:bg-gray-50'
                                        }>
                                            <td className={`px-4 py-3 ${
                                                theme === 'dark' ? 'text-white' : 'text-gray-900'
                                            }`}>
                                                <div>
                                                    <p className="font-medium">
                                                        {employee.first_name} {employee.last_name}
                                                    </p>
                                                    <p className={`text-sm ${
                                                        theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                                                    }`}>
                                                        {employee.employee_code}
                                                    </p>
                                                </div>
                                            </td>
                                            <td className={`px-4 py-3 text-sm ${
                                                theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                                            }`}>
                                                {employee.uan_number || 'Not assigned'}
                                            </td>
                                            <td className={`px-4 py-3 text-sm text-right ${
                                                theme === 'dark' ? 'text-green-400' : 'text-green-600'
                                            }`}>
                                                {balance ? formatCurrency(balance.epf_total) : '₹0.00'}
                                            </td>
                                            <td className={`px-4 py-3 text-sm text-right ${
                                                theme === 'dark' ? 'text-purple-400' : 'text-purple-600'
                                            }`}>
                                                {balance ? formatCurrency(balance.eps_contribution) : '₹0.00'}
                                            </td>
                                            <td className={`px-4 py-3 text-sm text-right ${
                                                theme === 'dark' ? 'text-amber-400' : 'text-amber-600'
                                            }`}>
                                                {balance ? formatCurrency(balance.epf_interest_earned + balance.eps_interest_earned) : '₹0.00'}
                                            </td>
                                            <td className={`px-4 py-3 text-sm text-right font-semibold ${
                                                theme === 'dark' ? 'text-white' : 'text-gray-900'
                                            }`}>
                                                {balance ? formatCurrency(balance.total_balance) : '₹0.00'}
                                            </td>
                                            <td className="px-4 py-3 text-center">
                                                <button
                                                    onClick={() => fetchEmployeePFDetails(employee.id)}
                                                    className={`px-3 py-1 text-xs rounded-lg transition-colors ${
                                                        theme === 'dark'
                                                            ? 'bg-blue-600 hover:bg-blue-700 text-white'
                                                            : 'bg-blue-500 hover:bg-blue-600 text-white'
                                                    }`}
                                                >
                                                    View Details
                                                </button>
                                            </td>
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        );
    };

    const renderReports = () => (
        <div className="space-y-6">
            <div className={`p-6 rounded-lg border ${
                theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
            }`}>
                <h3 className={`text-lg font-semibold mb-4 ${
                    theme === 'dark' ? 'text-white' : 'text-gray-900'
                }`}>
                    Generate PF Reports
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Monthly Report */}
                    <div className={`p-4 rounded-lg border ${
                        theme === 'dark' ? 'border-gray-700' : 'border-gray-200'
                    }`}>
                        <FileText className={`w-8 h-8 mb-3 ${
                            theme === 'dark' ? 'text-blue-400' : 'text-blue-600'
                        }`} />
                        <h4 className={`font-semibold mb-2 ${
                            theme === 'dark' ? 'text-white' : 'text-gray-900'
                        }`}>
                            Monthly PF Report
                        </h4>
                        <p className={`text-sm mb-4 ${
                            theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                        }`}>
                            Detailed month-wise PF contributions for all employees
                        </p>
                        <button className="w-full px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors">
                            Generate Report
                        </button>
                    </div>

                    {/* Challan Report */}
                    <div className={`p-4 rounded-lg border ${
                        theme === 'dark' ? 'border-gray-700' : 'border-gray-200'
                    }`}>
                        <Download className={`w-8 h-8 mb-3 ${
                            theme === 'dark' ? 'text-green-400' : 'text-green-600'
                        }`} />
                        <h4 className={`font-semibold mb-2 ${
                            theme === 'dark' ? 'text-white' : 'text-gray-900'
                        }`}>
                            PF Challan
                        </h4>
                        <p className={`text-sm mb-4 ${
                            theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                        }`}>
                            ECR challan for EPFO payment submission
                        </p>
                        <button
                            onClick={downloadPFChallan}
                            className="w-full px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors"
                        >
                            Download Challan
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );

    const renderConfiguration = () => (
        <div className="space-y-6">
            <div className={`p-6 rounded-lg border ${
                theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
            }`}>
                <h3 className={`text-lg font-semibold mb-6 ${
                    theme === 'dark' ? 'text-white' : 'text-gray-900'
                }`}>
                    PF Configuration
                </h3>

                {/* PF Enable/Disable Toggle */}
                <div className={`p-4 rounded-lg border mb-6 ${
                    theme === 'dark' ? 'bg-gray-900/50 border-gray-700' : 'bg-gray-50 border-gray-200'
                }`}>
                    <div className="flex items-center justify-between">
                        <div className="flex-1">
                            <h4 className={`font-semibold mb-1 ${
                                theme === 'dark' ? 'text-white' : 'text-gray-900'
                            }`}>
                                Enable Provident Fund (PF)
                            </h4>
                            <p className={`text-sm ${
                                theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                            }`}>
                                {pfEnabled
                                    ? 'PF deductions are currently enabled for all eligible employees'
                                    : 'PF deductions are currently disabled. Enable this if your company is registered with EPFO'
                                }
                            </p>
                        </div>
                        <button
                            onClick={() => setPfEnabled(!pfEnabled)}
                            className={`relative inline-flex h-8 w-14 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                                pfEnabled
                                    ? 'bg-blue-600 focus:ring-blue-500'
                                    : theme === 'dark'
                                        ? 'bg-gray-700 focus:ring-gray-600'
                                        : 'bg-gray-300 focus:ring-gray-400'
                            }`}
                        >
                            <span
                                className={`inline-block h-6 w-6 transform rounded-full bg-white transition-transform ${
                                    pfEnabled ? 'translate-x-7' : 'translate-x-1'
                                }`}
                            />
                        </button>
                    </div>
                </div>

                {/* Save Configuration Button */}
                <div className="mb-6">
                    <button
                        onClick={savePFConfig}
                        disabled={savingConfig}
                        className={`px-6 py-2 rounded-lg font-medium transition-colors ${
                            savingConfig
                                ? 'bg-gray-400 cursor-not-allowed'
                                : 'bg-blue-600 hover:bg-blue-700 text-white'
                        }`}
                    >
                        {savingConfig ? 'Saving...' : 'Save Configuration'}
                    </button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                        <label className={`block text-sm font-medium mb-2 ${
                            theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                        }`}>
                            Employee Contribution Rate
                        </label>
                        <input
                            type="text"
                            defaultValue="12%"
                            disabled
                            className={`w-full px-4 py-2 rounded-lg border ${
                                theme === 'dark'
                                    ? 'bg-gray-700 border-gray-600 text-gray-400'
                                    : 'bg-gray-100 border-gray-300 text-gray-600'
                            }`}
                        />
                    </div>

                    <div>
                        <label className={`block text-sm font-medium mb-2 ${
                            theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                        }`}>
                            Employer EPF Rate
                        </label>
                        <input
                            type="text"
                            defaultValue="3.67%"
                            disabled
                            className={`w-full px-4 py-2 rounded-lg border ${
                                theme === 'dark'
                                    ? 'bg-gray-700 border-gray-600 text-gray-400'
                                    : 'bg-gray-100 border-gray-300 text-gray-600'
                            }`}
                        />
                    </div>

                    <div>
                        <label className={`block text-sm font-medium mb-2 ${
                            theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                        }`}>
                            Employer EPS Rate
                        </label>
                        <input
                            type="text"
                            defaultValue="8.33%"
                            disabled
                            className={`w-full px-4 py-2 rounded-lg border ${
                                theme === 'dark'
                                    ? 'bg-gray-700 border-gray-600 text-gray-400'
                                    : 'bg-gray-100 border-gray-300 text-gray-600'
                            }`}
                        />
                    </div>

                    <div>
                        <label className={`block text-sm font-medium mb-2 ${
                            theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                        }`}>
                            PF Wage Ceiling
                        </label>
                        <input
                            type="text"
                            defaultValue="₹15,000"
                            disabled
                            className={`w-full px-4 py-2 rounded-lg border ${
                                theme === 'dark'
                                    ? 'bg-gray-700 border-gray-600 text-gray-400'
                                    : 'bg-gray-100 border-gray-300 text-gray-600'
                            }`}
                        />
                    </div>
                </div>

                <div className={`mt-6 p-4 rounded-lg ${
                    theme === 'dark' ? 'bg-blue-900/20' : 'bg-blue-50'
                }`}>
                    <p className={`text-sm ${
                        theme === 'dark' ? 'text-blue-300' : 'text-blue-800'
                    }`}>
                        <strong>Note:</strong> PF rates are as per EPF Act 1952 and cannot be modified. Contact HR support for any changes.
                    </p>
                </div>
            </div>
        </div>
    );

    // PF Details Modal
    const renderDetailsModal = () => {
        if (!showDetailsModal) return null;

        return (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                <div className={`max-w-5xl w-full max-h-[90vh] overflow-y-auto rounded-lg ${
                    theme === 'dark' ? 'bg-gray-800' : 'bg-white'
                }`}>
                    {/* Modal Header */}
                    <div className={`sticky top-0 p-6 border-b ${
                        theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
                    }`}>
                        <div className="flex items-center justify-between">
                            <div>
                                <h2 className={`text-xl font-bold ${
                                    theme === 'dark' ? 'text-white' : 'text-gray-900'
                                }`}>
                                    PF Details - {selectedEmployee?.full_name}
                                </h2>
                                <p className={`text-sm ${
                                    theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                                }`}>
                                    UAN: {selectedEmployee?.uan_number || 'Not assigned'} |
                                    PF Number: {selectedEmployee?.pf_number || 'Not assigned'}
                                </p>
                            </div>
                            <button
                                onClick={() => setShowDetailsModal(false)}
                                className={`p-2 rounded-lg ${
                                    theme === 'dark' ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
                                }`}
                            >
                                <span className={`text-2xl ${
                                    theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                                }`}>×</span>
                            </button>
                        </div>
                    </div>

                    {/* Modal Body */}
                    <div className="p-6 space-y-6">
                        {loadingDetails ? (
                            <div className="text-center py-8">
                                <p className={theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}>
                                    Loading PF details...
                                </p>
                            </div>
                        ) : (
                            <>
                                {/* Balance Summary */}
                                <div>
                                    <h3 className={`text-lg font-semibold mb-4 ${
                                        theme === 'dark' ? 'text-white' : 'text-gray-900'
                                    }`}>
                                        PF Balance Summary
                                    </h3>
                                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                                        <div className={`p-4 rounded-lg border ${
                                            theme === 'dark' ? 'bg-gray-700/50 border-gray-600' : 'bg-gray-50 border-gray-200'
                                        }`}>
                                            <p className={`text-sm mb-1 ${
                                                theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                                            }`}>
                                                EPF Employee Share
                                            </p>
                                            <p className={`text-xl font-bold ${
                                                theme === 'dark' ? 'text-green-400' : 'text-green-600'
                                            }`}>
                                                {pfDetails ? formatCurrency(pfDetails.epf_employee_share) : '₹0.00'}
                                            </p>
                                        </div>
                                        <div className={`p-4 rounded-lg border ${
                                            theme === 'dark' ? 'bg-gray-700/50 border-gray-600' : 'bg-gray-50 border-gray-200'
                                        }`}>
                                            <p className={`text-sm mb-1 ${
                                                theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                                            }`}>
                                                EPF Employer Share
                                            </p>
                                            <p className={`text-xl font-bold ${
                                                theme === 'dark' ? 'text-blue-400' : 'text-blue-600'
                                            }`}>
                                                {pfDetails ? formatCurrency(pfDetails.epf_employer_share) : '₹0.00'}
                                            </p>
                                        </div>
                                        <div className={`p-4 rounded-lg border ${
                                            theme === 'dark' ? 'bg-gray-700/50 border-gray-600' : 'bg-gray-50 border-gray-200'
                                        }`}>
                                            <p className={`text-sm mb-1 ${
                                                theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                                            }`}>
                                                Interest Earned
                                            </p>
                                            <p className={`text-xl font-bold ${
                                                theme === 'dark' ? 'text-amber-400' : 'text-amber-600'
                                            }`}>
                                                {pfDetails ? formatCurrency(pfDetails.epf_interest_earned + pfDetails.eps_interest_earned) : '₹0.00'}
                                            </p>
                                        </div>
                                        <div className={`p-4 rounded-lg border ${
                                            theme === 'dark' ? 'bg-gray-700/50 border-gray-600' : 'bg-gray-50 border-gray-200'
                                        }`}>
                                            <p className={`text-sm mb-1 ${
                                                theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                                            }`}>
                                                Total Balance
                                            </p>
                                            <p className={`text-xl font-bold ${
                                                theme === 'dark' ? 'text-white' : 'text-gray-900'
                                            }`}>
                                                {pfDetails ? formatCurrency(pfDetails.total_balance) : '₹0.00'}
                                            </p>
                                        </div>
                                    </div>

                                    <div className={`mt-4 p-4 rounded-lg border ${
                                        theme === 'dark' ? 'bg-gray-700/50 border-gray-600' : 'bg-blue-50 border-blue-200'
                                    }`}>
                                        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                                            <div>
                                                <p className={theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}>
                                                    EPS Contribution
                                                </p>
                                                <p className={`font-semibold ${
                                                    theme === 'dark' ? 'text-white' : 'text-gray-900'
                                                }`}>
                                                    {pfDetails ? formatCurrency(pfDetails.eps_contribution) : '₹0.00'}
                                                </p>
                                            </div>
                                            <div>
                                                <p className={theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}>
                                                    Total Months
                                                </p>
                                                <p className={`font-semibold ${
                                                    theme === 'dark' ? 'text-white' : 'text-gray-900'
                                                }`}>
                                                    {pfDetails?.total_months_contributed || 0}
                                                </p>
                                            </div>
                                            <div>
                                                <p className={theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}>
                                                    As of Date
                                                </p>
                                                <p className={`font-semibold ${
                                                    theme === 'dark' ? 'text-white' : 'text-gray-900'
                                                }`}>
                                                    {pfDetails?.as_of_date || '-'}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* PF Passbook */}
                                <div>
                                    <h3 className={`text-lg font-semibold mb-4 ${
                                        theme === 'dark' ? 'text-white' : 'text-gray-900'
                                    }`}>
                                        PF Passbook
                                    </h3>
                                    <div className={`overflow-x-auto rounded-lg border ${
                                        theme === 'dark' ? 'border-gray-700' : 'border-gray-200'
                                    }`}>
                                        <table className="w-full text-sm">
                                            <thead className={theme === 'dark' ? 'bg-gray-700' : 'bg-gray-50'}>
                                                <tr>
                                                    <th className={`px-4 py-2 text-left text-xs font-semibold ${
                                                        theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                                                    }`}>Month</th>
                                                    <th className={`px-4 py-2 text-right text-xs font-semibold ${
                                                        theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                                                    }`}>Employee</th>
                                                    <th className={`px-4 py-2 text-right text-xs font-semibold ${
                                                        theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                                                    }`}>Employer</th>
                                                    <th className={`px-4 py-2 text-right text-xs font-semibold ${
                                                        theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                                                    }`}>Total</th>
                                                </tr>
                                            </thead>
                                            <tbody className={`divide-y ${
                                                theme === 'dark' ? 'divide-gray-700' : 'divide-gray-200'
                                            }`}>
                                                {pfHistory.length === 0 ? (
                                                    <tr>
                                                        <td colSpan="4" className="px-4 py-4 text-center">
                                                            <span className={theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}>
                                                                No PF contributions recorded
                                                            </span>
                                                        </td>
                                                    </tr>
                                                ) : (
                                                    pfHistory.map((entry) => (
                                                        <tr key={entry.id} className={
                                                            theme === 'dark' ? 'bg-gray-800/50' : 'bg-white'
                                                        }>
                                                            <td className={`px-4 py-2 ${
                                                                theme === 'dark' ? 'text-white' : 'text-gray-900'
                                                            }`}>
                                                                {entry.month_year}
                                                            </td>
                                                            <td className={`px-4 py-2 text-right ${
                                                                theme === 'dark' ? 'text-green-400' : 'text-green-600'
                                                            }`}>
                                                                {formatCurrency(entry.total_employee_contribution)}
                                                            </td>
                                                            <td className={`px-4 py-2 text-right ${
                                                                theme === 'dark' ? 'text-purple-400' : 'text-purple-600'
                                                            }`}>
                                                                {formatCurrency(entry.total_employer_contribution)}
                                                            </td>
                                                            <td className={`px-4 py-2 text-right font-semibold ${
                                                                theme === 'dark' ? 'text-white' : 'text-gray-900'
                                                            }`}>
                                                                {formatCurrency(entry.total_pf_amount)}
                                                            </td>
                                                        </tr>
                                                    ))
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </>
                        )}
                    </div>

                    {/* Modal Footer */}
                    <div className={`sticky bottom-0 p-4 border-t ${
                        theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
                    }`}>
                        <button
                            onClick={() => setShowDetailsModal(false)}
                            className={`px-6 py-2 rounded-lg font-medium transition-colors ${
                                theme === 'dark'
                                    ? 'bg-gray-700 hover:bg-gray-600 text-white'
                                    : 'bg-gray-200 hover:bg-gray-300 text-gray-900'
                            }`}
                        >
                            Close
                        </button>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className={`tour-comp-pf min-h-screen p-6 ${
            theme === 'dark' ? 'bg-gray-900' : 'bg-gray-50'
        }`}>
            <div className="max-w-7xl mx-auto">
                {/* Header */}
                <div className="mb-6">
                    <h1 className={`text-2xl font-bold mb-2 ${
                        theme === 'dark' ? 'text-white' : 'text-gray-900'
                    }`}>
                        Provident Fund Management
                    </h1>
                    <p className={theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}>
                        Manage employee PF, generate reports, and track contributions
                    </p>
                </div>

                {/* Tabs */}
                <div className={`border-b mb-6 ${
                    theme === 'dark' ? 'border-gray-700' : 'border-gray-200'
                }`}>
                    <div className="flex gap-1 overflow-x-auto">
                        {tabs.map((tab) => (
                            <button
                                key={tab}
                                onClick={() => setActiveTab(tab)}
                                className={`px-6 py-3 text-sm font-medium whitespace-nowrap transition-colors ${
                                    activeTab === tab
                                        ? theme === 'dark'
                                            ? 'text-blue-400 border-b-2 border-blue-400'
                                            : 'text-blue-600 border-b-2 border-blue-600'
                                        : theme === 'dark'
                                            ? 'text-gray-400 hover:text-gray-300'
                                            : 'text-gray-600 hover:text-gray-900'
                                }`}
                            >
                                {tab}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Tab Content */}
                <div>
                    {activeTab === 'OVERVIEW' && renderOverview()}
                    {activeTab === 'EMPLOYEE PF' && renderEmployeePF()}
                    {activeTab === 'REPORTS' && renderReports()}
                    {activeTab === 'CONFIGURATION' && renderConfiguration()}
                </div>
            </div>

            {/* PF Details Modal */}
            {renderDetailsModal()}
        </div>
    );
};

export default PFManagement;
